<template>
  <v-card v-if="organization"
    ><v-card-title
      ><span v-if="!organization.serviceAreaId">Update</span>&nbsp;Service Area
      for {{ organization.name }}</v-card-title
    >
    <v-card-text>
      <v-alert
        class="mt-1"
        outlined
        type="error"
        v-if="!organization.contractSigned"
      >
        This organization's contract has not been signed. You cannot assign a
        Service Area until the contract is signed. (Business Development updates
        this status within Valet.)
      </v-alert>
    </v-card-text>
    <v-card-text v-if="organization.contractSigned">
      <div class="ml-5">
        <v-form @submit.prevent="onSubmit" ref="form" v-model="valid">
          <v-alert
            class="mt-1"
            outlined
            type="error"
            v-if="!organization.serviceAreaId"
          >
            Once assigned, a Service Area ID cannot be edited. If you need to
            edit the Service Area, contact the Tools Team at valet@ochin.org for
            assistance.
          </v-alert>
          <label class="och-label" v-if="!organization.serviceAreaId"
            >Service Area ID</label
          >

          <unique-text-field
            :disabled="
              !organization.contractSigned || !!organization.serviceAreaId
            "
            :entityIdValue="organization.organizationId"
            :list="organizationList"
            :rules="ruleList"
            attributeName="serviceAreaId"
            entityName="organization"
            outlined
            data-cy="serviceAreaId"
            v-model="serviceAreaId"
            v-if="!organization.serviceAreaId"
          ></unique-text-field>
          <div class="mt-1">
            <div>
              <v-icon
                :aria-label="`Service Area is updating`"
                aria-hidden="false"
                class="spinner"
                key="updatingServiceArea"
                small
                v-if="updatingServiceArea"
                width="10px"
                >fa fa-spinner</v-icon
              >
              <div v-if="this.serviceAreaCreated">
                <v-icon class="mr-2" color="accent">fa fa-check</v-icon>Service
                Area ID {{ organization.serviceAreaId }} assigned to
                organization
              </div>
            </div>

            <div>
              <v-icon
                :aria-label="`Crowd Group is updating`"
                aria-hidden="false"
                class="spinner"
                key="creatingCrowdGroup"
                small
                v-if="creatingCrowdGroup"
                width="10px"
                >fa fa-spinner</v-icon
              >
              <div v-if="this.crowdGroupCreated">
                <v-icon class="mr-2" color="accent">fa fa-check</v-icon>Crowd
                Group created (or existing crowd group added to organization)
              </div>
            </div>

            <div>
              <v-icon
                :aria-label="`Valet User List is syncing`"
                aria-hidden="false"
                class="spinner"
                key="syncingValetUserList"
                small
                v-if="syncingValetUserList"
                width="10px"
                >fa fa-spinner</v-icon
              >
              <div v-if="this.userListSynced">
                <v-icon class="mr-2" color="accent">fa fa-check</v-icon>Valet
                Users for {{ organization.name }} added to Crowd Group
                {{ organization.crowdGroup }}
              </div>
            </div>
          </div>
        </v-form>
      </div>
    </v-card-text>
    <v-card-text v-if="organization.contractSigned">
      <v-alert class="mt-2" color="primary" outlined v-if="syncResult">
        <div class="och-label">Results</div>
        <div>Users Synced: {{ syncResult.syncedUserList.length || "0" }}</div>
        <div>
          Users Not Synced due to Error:
          {{ syncResult.unsyncedUserList.length || "0" }}
        </div>
      </v-alert></v-card-text
    >
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        @click="cancel"
        outlined
        v-if="!organization.serviceAreaId && organization.contractSigned"
        >Cancel</v-btn
      >
      <v-btn
        @click="updateServiceArea"
        color="primary"
        data-cy="updateServiceArea"
        outlined
        :disabled="!!organization.serviceAreaId || !valid"
        v-if="!organization.serviceAreaId"
        >Assign Service Area ID</v-btn
      >
      <v-btn
        @click="$emit('setDialog', false)"
        color="primary"
        data-cy="ok"
        outlined
        v-if="!organization.contractSigned || organization.serviceAreaId"
        >OK</v-btn
      ></v-card-actions
    >
  </v-card>
</template>

<script>
import { cloneDeep } from "lodash";
import { mapActions, mapState } from "vuex";
import UniqueTextField from "@/components/static/UniqueTextField";

export default {
  components: {
    UniqueTextField,
  },
  computed: {
    ...mapState({
      organizationList: state => state.organization.listAdmin,
      ochinUserList: state => state.coreTeam.ochinUserList,
      salesforceList: state => state.salesforce.list,
    }),
  },
  async created() {
    if (this.organization.serviceAreaId) {
      this.serviceAreaId = cloneDeep(this.organization.serviceAreaId);
      this.serviceAreaCreated = true;
    }
    if (this.organization.crowdGroup) {
      this.crowdGroupCreated = true;
    }
  },
  data() {
    return {
      creatingCrowdGroup: false,
      crowdGroupCreated: false,
      dialog: false,
      syncingValetUserList: false,
      ruleList: [
        value => !!value || "Service Area is required.",
        value =>
          (!!value && value.length <= 3) ||
          "Service Area ID must be 3 or fewer characters.",
      ],
      serviceAreaCreated: false,
      serviceAreaId: null,
      syncResult: null,
      updatingServiceArea: false,
      userListSynced: false,
      valid: false,
    };
  },
  methods: {
    cancel() {
      this.$emit("setDialog", false);
      this.serviceAreaId = null;
    },
    async updateServiceArea() {
      // First, save the new Service Area ID to the organization record in Valet.
      try {
        this.updatingServiceArea = true;
        await this.updateOrganization([
          this.organization.organizationId,
          { serviceAreaId: parseInt(this.serviceAreaId) },
        ]);
        this.serviceAreaCreated = true;
        this.updatingServiceArea = false;
        this.fetchOrganizationList();
      } catch (e) {
        this.updatingServiceArea = false;
        console.error(e);
        return this.$notifyError({
          message: `Could not update Service Area ID for ${this.organization.name}`,
          timeout: 0,
        });
      }
    },
    ...mapActions({
      fetchOrganizationList: "organization/listAdmin",
      updateOrganization: "organizationAdmin/update",
    }),
  },
  props: {
    organization: {
      default: null,
    },
  },
};
</script>
